import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, EnvelopeIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import LoadingSpinner from '../ui/LoadingSpinner';

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: () => void;
  userEmail: string;
}



const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  isOpen,
  onClose,
  onVerified,
  userEmail,
}) => {
  const { t } = useTranslation();
  const [verificationSent, setVerificationSent] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState(20000); // Start with 20 seconds
  const [timeSinceLastResend, setTimeSinceLastResend] = useState(0);
  const [canResend, setCanResend] = useState(false);

  // Check verification status
  const checkVerificationStatus = useCallback(async () => {
    try {
      setIsChecking(true);
      setError(null);
      
      const response = await axios.get('/api/auth/me');
      const user = response.data.data.user;
      
      if (user.emailVerified) {
        onVerified();
        return;
      }
      
      // If not verified, continue polling
    } catch (error: any) {
      console.error('Error checking verification status:', error);
      setError(t('emailVerification.checkError', 'Failed to check verification status'));
    } finally {
      setIsChecking(false);
    }
  }, [onVerified, t]);

  // Send verification email
  const sendVerificationEmail = useCallback(async () => {
    try {
      setIsResending(true);
      setError(null);
      
      await axios.post('/api/auth/send-email-verification');
      setVerificationSent(true);
      setTimeSinceLastResend(0);
      setCanResend(false);
      
      // Start counting down to allow resend
      const resendTimer = setInterval(() => {
        setTimeSinceLastResend(prev => {
          const newTime = prev + 1;
          if (newTime >= 30) {
            setCanResend(true);
            clearInterval(resendTimer);
          }
          return newTime;
        });
      }, 1000);
      
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      setError(t('emailVerification.sendError', 'Failed to send verification email'));
    } finally {
      setIsResending(false);
    }
  }, [t]);

  // Handle resend
  const handleResend = async () => {
    if (!canResend) return;
    await sendVerificationEmail();
  };

  // Polling effect
  useEffect(() => {
    if (!isOpen || !verificationSent) return;

    const poll = async () => {
      await checkVerificationStatus();
    };

    const interval = setInterval(poll, pollingInterval);

    // Increase polling interval gradually (20s -> 30s -> 45s -> 60s)
    const intervalIncrease = setInterval(() => {
      setPollingInterval(prev => {
        if (prev >= 60000) return prev; // Max 60 seconds
        return Math.min(prev * 1.5, 60000);
      });
    }, 60000); // Increase every minute

    return () => {
      clearInterval(interval);
      clearInterval(intervalIncrease);
    };
  }, [isOpen, verificationSent, pollingInterval, checkVerificationStatus]);

  // Initial send when modal opens
  useEffect(() => {
    if (isOpen && !verificationSent) {
      sendVerificationEmail();
    }
  }, [isOpen, verificationSent, sendVerificationEmail]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <EnvelopeIcon className="h-6 w-6 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('emailVerification.title', 'Verify Your Email')}
          </h3>
          <p className="text-sm text-gray-600">
            {t('emailVerification.subtitle', 'We need to verify your email address to continue')}
          </p>
        </div>

        {/* Email display */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">
                {t('emailVerification.emailSentTo', 'Email sent to:')}
              </p>
              <p className="text-sm text-gray-900 font-mono">{userEmail}</p>
            </div>
            {verificationSent && (
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">
              {t('emailVerification.instructions.title', 'What to do next:')}
            </h4>
            <ol className="text-sm text-blue-800 space-y-1">
              <li>1. {t('emailVerification.instructions.step1', 'Check your email inbox')}</li>
              <li>2. {t('emailVerification.instructions.step2', 'Click the verification link in the email')}</li>
              <li>3. {t('emailVerification.instructions.step3', 'Return here and we\'ll automatically detect verification')}</li>
            </ol>
          </div>
        </div>

        {/* Status and actions */}
        <div className="space-y-4">
          {/* Checking status */}
          {isChecking && (
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
              <LoadingSpinner size="sm" />
              <span>{t('emailVerification.checking', 'Checking verification status...')}</span>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex items-center space-x-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Resend section */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {canResend ? (
                  <span>{t('emailVerification.canResend', 'Didn\'t receive the email?')}</span>
                ) : (
                  <span>
                    {t('emailVerification.resendIn', 'You can request another email in {{seconds}} seconds', {
                      seconds: 30 - timeSinceLastResend
                    })}
                  </span>
                )}
              </div>
              <button
                onClick={handleResend}
                disabled={!canResend || isResending}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  canResend && !isResending
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isResending ? (
                  <div className="flex items-center space-x-2">
                    <LoadingSpinner size="sm" />
                    <span>{t('emailVerification.sending', 'Sending...')}</span>
                  </div>
                ) : (
                  t('emailVerification.resend', 'Resend Email')
                )}
              </button>
            </div>
          </div>

          {/* Polling info */}
          <div className="text-xs text-gray-500 text-center">
            {t('emailVerification.pollingInfo', 'We\'ll check for verification every {{interval}} seconds', {
              interval: Math.round(pollingInterval / 1000)
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationModal; 